import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import {
  getRequest,
  optimisticOptions,
  putRequest,
  queries,
} from "../../../../react-query";
import { validateForm } from "../../../../utils";
import JsonView from 'react-json-view-ts';
import Editor from "@monaco-editor/react";
import { toast } from "react-toastify";
import getStorage from "../../../../services/storageService";


interface FormData {
  activities_data: any[];
}

export const UpdateConfig = () => {
  const navigate = useNavigate();
  const { uin_id } = useParams();
  const [formData, setFormData] = useState<FormData>({
    activities_data: []
  });
  const client_id = getStorage('client_id');
  const [isTreeView, setIsTreeView] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: configData = {}, isLoading: isConfigLoading }: any = useQuery(
    [queries.config, uin_id],
    () => getRequest(`/config/${uin_id}?client_id=${client_id}`),
    {
      enabled: !!uin_id,
    }
  );

  const { data: config = {} } = configData;


  const onChangeHandler = (value: string, name: string) => {
    try {
      if (value.trim()) {
        // Only try to parse if the string looks like it might be valid JSON
        if (value.trim().startsWith('{') && value.trim().endsWith('}')) {
          const parsedValue = JSON.parse(value.trim());
          setFormData({
            ...formData,
            [name]: parsedValue,
          });
        }
      }
    } catch (error) {
      //console.error('Invalid JSON:', error);
      // Don't update form data if JSON is invalid
    }
  };

  const { mutate: updateConfig } = useMutation(
    putRequest,
    optimisticOptions(queries.config)
  );

  // Define validation rules
  const validationRules = {
    activities_data: [
      { required: true, message: "Activities data is required." },
    ],
  };

  const onSubmitHandler = useCallback(
    (e: any) => {
      e.preventDefault();

      // Validate form data
      const formErrors = validateForm(formData, validationRules);
      setErrors(formErrors);

      if (Object.keys(formErrors).length === 0) {
        updateConfig({
          endPoint: `/config/${config.uin_id}`,
          payload: {activities_data: formData.activities_data, uin_id: config.uin_id, client_id: config.client_id},
        });
        toast.success("Configuration saved successfully");
        navigate(-1);
      } else {
        toast.error('Please check all fields');
      }
    },
    [formData, config, updateConfig]
  );

  useEffect(() => {
    if (config.uin_id) {
      setFormData({
        activities_data: config.activities_data || []
      });
    }
  }, [config]);

  if (isConfigLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="bg-white w-full h-full p-8 space-y-6">
      <div className="flex w-full items-center justify-between border-b pb-4">
        <div className="flex items-center space-x-4">
          <ArrowLeftIcon
            onClick={() => navigate("/settings")}
            className="w-6 h-6 text-gray-600 hover:text-blue-500 cursor-pointer transition-colors duration-200"
          />
          <h1 className="text-2xl font-bold text-gray-800">Update Configuration</h1>
        </div>
        <button
          type="button"
          onClick={() => setIsTreeView(!isTreeView)}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 flex items-center space-x-2"
        >
          <span>{isTreeView ? "Switch to Editor" : "Switch to Tree View"}</span>
        </button>
      </div>

      <form
        onSubmit={onSubmitHandler}
        className="w-full flex-1 min-h-0 flex flex-col justify-between"
        style={{ height: 'calc(100vh - 200px)' }}
      >
        <div className="space-y-4 flex-1 min-h-0 flex flex-col">
          <div className="bg-gray-50 rounded-lg p-4 flex-1 min-h-0 flex flex-col overflow-auto">
            {isTreeView ? (
              <JsonView
                src={formData.activities_data}
                onEdit={(val: any) => onChangeHandler(val, "activities_data")}
                onAdd={(val: any) => onChangeHandler(val, "activities_data")}
                onDelete={(val: any) => onChangeHandler(val, "activities_data")}
                theme={{
                  base00: 'var(--background)',
                  base01: 'var(--muted)',
                  base02: 'var(--accent)',
                  base03: 'var(--muted-foreground)',
                  base04: 'var(--muted-foreground)',
                  base05: 'var(--foreground)',
                  base06: 'var(--foreground)',
                  base07: 'var(--foreground)',
                  base08: 'var(--destructive)',
                  base09: 'var(--warning)',
                  base0A: 'var(--warning)',
                  base0B: 'var(--success)',
                  base0C: 'var(--info)',
                  base0D: 'var(--primary)',
                  base0E: 'var(--secondary)',
                  base0F: 'var(--destructive)',
                }}
                displayDataTypes={false}
                enableClipboard={true}
                style={{
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  backgroundColor: 'transparent',
                  height: '100%',
                }}
              />
            ) : (
              <div className="flex-1 min-h-0 flex flex-col overflow-auto">
                <Editor
                  height="100%"
                  defaultLanguage="json"
                  value={JSON.stringify(formData.activities_data, null, 2)}
                  onChange={(val: any) => onChangeHandler(val, "activities_data")}
                  theme="vs-dark"
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    formatOnPaste: true,
                    formatOnType: true,
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    wrappingIndent: "indent",
                    automaticLayout: true,
                    tabSize: 2,
                    lineNumbers: "on",
                    renderWhitespace: "selection",
                    bracketPairColorization: {
                      enabled: true
                    }
                  }}
                />
              </div>
            )}
          </div>
          {errors.activities_data && (
            <p className="text-sm text-red-600 bg-red-50 p-2 rounded-md">{errors.activities_data}</p>
          )}
        </div>

        <div className="flex justify-end items-center space-x-4 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors duration-200"
          >
            Save Configuration
          </button>
        </div>
      </form>
    </div>
  );
};